import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';

import Logo from '../resources/images/logos/FullColourLogo.png';

import { generateNavItems } from '../lib/menuItems';

const ORANGE = '#cb6423';
const BLACK = '#000000';
const WHITE = '#FFFFFF';
const BORDER = '#E5E5E5';

const MOBILE_MENU_TRANSITION = 'transform 500ms cubic-bezier(0.16, 1, 0.3, 1), opacity 400ms ease';

const SUBMENU_TRANSITION = 'transform 500ms cubic-bezier(0.16, 1, 0.3, 1), opacity 400ms ease';

const navIcons = {
  'Portfolio Overview': AccountBalanceOutlinedIcon,
  'My Clients': PeopleAltOutlinedIcon,
  'Client Services': SupportAgentOutlinedIcon,
  'My Practice': BusinessOutlinedIcon,
  'System Admin': AdminPanelSettingsOutlinedIcon
};

const SideNav = ({ user }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(true);

  /*
   * Desktop accordion state.
   */
  const [openItems, setOpenItems] = useState({});

  /*
   * Mobile navigation state.
   *
   * null = root menu
   * object = currently opened submenu
   *
   * This intentionally does NOT reset when the mobile menu
   * closes, allowing the last opened submenu to remain selected.
   */
  const [mobileMenu, setMobileMenu] = useState(null);

  const [hasAppHistory, setHasAppHistory] = useState(false);

  const navItems = useMemo(() => generateNavItems(user), [user]);

  /**
   * Determines whether a route is active.
   *
   * /no-personal-accounts behaves exactly like /balances.
   */
  const isChildActive = useCallback(
    (link) => {
      const pathname = location.pathname;

      if (!link) {
        return false;
      }

      if (link === '/balances') {
        return (
          pathname === '/balances' ||
          pathname.startsWith('/balances/') ||
          pathname === '/no-personal-accounts'
        );
      }

      return pathname === link || pathname.startsWith(`${link}/`);
    },
    [location.pathname]
  );

  /**
   * Determines whether a parent has an active child.
   */
  const isParentActive = useCallback(
    (item) => {
      return item.items?.some((subItem) => isChildActive(subItem.link));
    },
    [isChildActive]
  );

  /**
   * Determines whether the parent link itself is active.
   */
  const isParentLinkActive = useCallback(
    (item) => {
      if (!item?.link) {
        return false;
      }

      if (item.link === '/balances') {
        return (
          location.pathname === '/balances' ||
          location.pathname.startsWith('/balances/') ||
          location.pathname === '/no-personal-accounts'
        );
      }

      return location.pathname === item.link || location.pathname.startsWith(`${item.link}/`);
    },
    [location.pathname]
  );

  /**
   * Keep the parent containing the current route open on desktop.
   */
  useEffect(() => {
    const activeParent = navItems.find((item) => isParentActive(item));

    if (activeParent) {
      setOpenItems((prev) => ({
        ...prev,
        [activeParent.title]: true
      }));
    }
  }, [navItems, isParentActive]);

  useEffect(() => {
    if (location.key !== 'default') {
      setHasAppHistory(true);
    }
  }, [location.key]);

  /**
   * Mobile:
   *
   * Close navigation whenever the route changes.
   *
   * IMPORTANT:
   * mobileMenu is NOT reset here.
   *
   * So if the user:
   *
   * 1. Opens My Clients
   * 2. Closes the menu
   * 3. Opens the menu again
   *
   * My Clients remains the selected submenu.
   */
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
      setMobileMenu(null);
    }
  }, [location.pathname, isMobile]);

  /**
   * Desktop parent accordion behavior.
   */
  const handleParentClick = (item) => {
    const hasChildren = item.items?.length > 0;

    if (!hasChildren) {
      return;
    }

    const currentlyOpen = !!openItems[item.title];

    setOpenItems((prev) => {
      const next = {};

      navItems.forEach((navItem) => {
        const isCurrent = navItem.title === item.title;
        const isActive = isParentActive(navItem);

        if (isCurrent) {
          next[navItem.title] = !currentlyOpen;
        } else if (isActive) {
          next[navItem.title] = true;
        } else {
          next[navItem.title] = false;
        }
      });

      return next;
    });
  };

  /**
   * Mobile:
   *
   * Open a submenu instead of expanding it inline.
   */
  const handleMobileParentClick = (item) => {
    const hasChildren = item.items?.length > 0;

    if (!hasChildren) {
      setOpen(false);
      return;
    }

    setMobileMenu(item);
  };

  /**
   * Mobile:
   *
   * Return from a submenu to the root menu.
   */
  const handleMobileBack = () => {
    setMobileMenu(null);
  };

  /**
   * Close mobile drawer when child is selected.
   *
   * mobileMenu intentionally remains unchanged.
   */
  const handleChildClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  /**
   * Close mobile drawer when Profile is selected.
   */
  const handleProfileClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  /**
   * Desktop toggle.
   */
  const handleDesktopToggle = () => {
    setOpen((prev) => !prev);
  };

  /**
   * Mobile open.
   *
   * mobileMenu intentionally remains unchanged.
   */
  const handleMobileOpen = () => {
    setOpen(true);
  };

  /**
   * Mobile close.
   *
   * mobileMenu intentionally remains unchanged.
   */
  const handleMobileClose = () => {
    setOpen(false);
  };

  const profileActive =
    location.pathname === '/user/profile' || location.pathname.startsWith('/user/profile/');

  /**
   * ============================================================
   * MOBILE HEADER
   * ============================================================
   */
  const MobileHeader = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 72,

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',

        backgroundColor: WHITE,
        borderBottom: `1px solid ${BORDER}`,

        zIndex: 1400,

        px: 2
      }}>
      {/* LOGO */}
      <Box
        component="img"
        src={Logo}
        alt="Fairtree"
        sx={{
          width: 145,
          height: 'auto',
          maxHeight: 44,

          objectFit: 'contain',
          objectPosition: 'left center'
        }}
      />

      {/* RIGHT ACTIONS */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',

          /*
           * Keep the profile and hamburger icons close together.
           */
          gap: 0
        }}>
        {/* MY PROFILE */}
        <IconButton
          component={NavLink}
          to="/user/profile"
          onClick={handleProfileClick}
          aria-label="My Profile"
          disableRipple
          sx={{
            width: 36,
            height: 36,

            color: profileActive ? ORANGE : BLACK,

            p: 0,

            transition: 'color 250ms ease, transform 200ms ease',

            '&:hover': {
              backgroundColor: 'transparent',
              color: ORANGE
            },

            '&:active': {
              transform: 'scale(0.9)'
            }
          }}>
          <PersonOutlinedIcon
            sx={{
              fontSize: 25
            }}
          />
        </IconButton>

        {/* MENU */}
        <IconButton
          onClick={open ? handleMobileClose : handleMobileOpen}
          aria-label={open ? 'Close navigation' : 'Open navigation'}
          disableRipple
          sx={{
            width: 36,
            height: 36,

            color: BLACK,

            p: 0,

            transition: 'color 250ms ease, transform 200ms ease',

            '&:hover': {
              backgroundColor: 'transparent',
              color: ORANGE
            },

            '&:active': {
              transform: 'scale(0.9)'
            }
          }}>
          {open ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  /**
   * ============================================================
   * MOBILE MENU
   * ============================================================
   */
  const MobileMenu = () => {
    if (!isMobile) {
      return null;
    }

    return (
      <>
        {/* BACKDROP */}
        <Box
          onClick={handleMobileClose}
          sx={{
            position: 'fixed',
            inset: 0,
            top: 72,

            backgroundColor: 'rgba(0, 0, 0, 0.25)',

            zIndex: 1299,

            opacity: open ? 1 : 0,

            visibility: open ? 'visible' : 'hidden',

            transition: 'opacity 450ms cubic-bezier(0.16, 1, 0.3, 1), visibility 450ms',

            pointerEvents: open ? 'auto' : 'none'
          }}
        />

        {/* MOBILE MENU */}
        <Box
          sx={{
            position: 'fixed',
            top: 72,
            left: 0,
            right: 0,
            bottom: 0,

            backgroundColor: WHITE,

            zIndex: 1300,

            overflow: 'hidden',

            transform: open ? 'translateY(0)' : 'translateY(-24px)',
            opacity: open ? 1 : 0,

            transition: MOBILE_MENU_TRANSITION,

            boxShadow: open ? '0 12px 30px rgba(0, 0, 0, 0.08)' : 'none',

            pointerEvents: open ? 'auto' : 'none',
            visibility: open ? 'visible' : 'hidden'
          }}>
          {/* ==================================================== */}
          {/* ROOT MENU                                             */}
          {/* ==================================================== */}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,

              display: 'flex',
              flexDirection: 'column',

              backgroundColor: WHITE,

              /*
               * When a submenu opens, the root menu slides
               * out towards the left.
               */
              transform: mobileMenu ? 'translateX(-100%)' : 'translateX(0)',

              opacity: mobileMenu ? 0 : 1,

              transition: SUBMENU_TRANSITION,

              pointerEvents: open && !mobileMenu ? 'auto' : 'none'
            }}>
            {/* MENU CONTENT */}
            <Box
              sx={{
                flex: 1,

                overflowY: 'auto',
                overflowX: 'hidden',

                py: 1,
                px: 2
              }}>
              <List disablePadding>
                {navItems.map((item) => {
                  const hasChildren = item.items?.length > 0;

                  const parentActive = isParentActive(item) || isParentLinkActive(item);

                  const ParentIcon = navIcons[item.title];

                  return (
                    <ListItemButton
                      key={item.title}
                      component={!hasChildren ? NavLink : 'button'}
                      to={!hasChildren ? item.link : undefined}
                      onClick={() => handleMobileParentClick(item)}
                      sx={{
                        minHeight: 58,
                        width: '100%',

                        px: 1.5,
                        mb: 0.5,

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',

                        /*
                         * ACTIVE = COLOR ONLY.
                         */
                        color: parentActive ? ORANGE : BLACK,

                        backgroundColor: 'transparent !important',

                        textDecoration: 'none',

                        borderBottom: `1px solid ${BORDER}`,

                        borderRadius: 0,

                        transition: 'color 250ms ease, transform 200ms ease',

                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: ORANGE
                        },

                        '&:active': {
                          transform: 'translateX(4px)'
                        }
                      }}>
                      {ParentIcon && (
                        <ListItemIcon
                          sx={{
                            minWidth: 42,

                            color: 'inherit',

                            justifyContent: 'center'
                          }}></ListItemIcon>
                      )}

                      <Typography
                        sx={{
                          flex: 1,

                          fontFamily: 'Raleway, sans-serif',

                          fontSize: 15,

                          fontWeight: parentActive ? 600 : 500,

                          lineHeight: 1.4,

                          color: 'inherit'
                        }}>
                        {item.title}
                      </Typography>

                      {hasChildren && (
                        <ChevronRightIcon
                          sx={{
                            fontSize: 22,
                            color: 'inherit',

                            transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)'
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            {/* PROFILE */}
            <Box
              sx={{
                flexShrink: 0,

                px: 2,
                py: 1.5,

                borderTop: `1px solid ${BORDER}`,

                backgroundColor: WHITE
              }}>
              <ListItemButton
                component={NavLink}
                to="/user/profile"
                onClick={handleProfileClick}
                sx={{
                  minHeight: 56,

                  px: 1.5,

                  /*
                   * ACTIVE = COLOR ONLY.
                   */
                  color: profileActive ? ORANGE : BLACK,

                  backgroundColor: 'transparent !important',

                  textDecoration: 'none',

                  borderRadius: 0,

                  transition: 'color 250ms ease, transform 200ms ease',

                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: ORANGE
                  },

                  '&:active': {
                    transform: 'translateX(4px)'
                  }
                }}>
                <ListItemIcon
                  sx={{
                    minWidth: 42,

                    justifyContent: 'center',

                    color: 'inherit'
                  }}></ListItemIcon>

                <Typography
                  sx={{
                    fontFamily: 'Raleway, sans-serif',

                    fontSize: 14,

                    fontWeight: profileActive ? 600 : 500,

                    color: 'inherit'
                  }}>
                  My Profile
                </Typography>
              </ListItemButton>
            </Box>
          </Box>

          {/* ==================================================== */}
          {/* SUBMENU                                               */}
          {/* ==================================================== */}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,

              display: 'flex',
              flexDirection: 'column',

              backgroundColor: WHITE,

              /*
               * SUBMENU SLIDES IN FROM THE RIGHT.
               */
              transform: mobileMenu ? 'translateX(0)' : 'translateX(100%)',

              opacity: mobileMenu ? 1 : 0,

              transition: SUBMENU_TRANSITION,

              pointerEvents: mobileMenu ? 'auto' : 'none'
            }}>
            {/* SUBMENU HEADER */}
            <Box
              sx={{
                height: 64,
                minHeight: 64,

                display: 'flex',
                alignItems: 'center',

                borderBottom: `1px solid ${BORDER}`,

                px: 1
              }}>
              <IconButton
                onClick={handleMobileBack}
                aria-label="Back"
                disableRipple
                sx={{
                  width: 44,
                  height: 44,

                  color: BLACK,

                  transition: 'color 250ms ease, transform 200ms ease',

                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: ORANGE
                  },

                  '&:active': {
                    transform: 'translateX(-3px)'
                  }
                }}>
                <ChevronLeftIcon />
              </IconButton>

              <Typography
                sx={{
                  ml: 1,

                  fontFamily: 'Raleway, sans-serif',

                  fontSize: 16,

                  fontWeight: 600,

                  color: BLACK
                }}>
                {mobileMenu?.title}
              </Typography>
            </Box>

            {/* SUBMENU CONTENT */}
            <Box
              sx={{
                flex: 1,

                overflowY: 'auto',
                overflowX: 'hidden',

                py: 1,
                px: 2
              }}>
              <List disablePadding>
                {mobileMenu?.items?.map((item) => {
                  const active = isChildActive(item.link);

                  const hasChildren = item.items?.length > 0;

                  return (
                    <ListItemButton
                      key={item.link || item.text}
                      component={!hasChildren ? NavLink : 'button'}
                      to={!hasChildren ? item.link : undefined}
                      onClick={() =>
                        hasChildren ? handleMobileParentClick(item) : handleChildClick()
                      }
                      sx={{
                        minHeight: 58,
                        width: '100%',

                        px: 1.5,
                        mb: 0.5,

                        display: 'flex',
                        alignItems: 'center',

                        /*
                         * ACTIVE = COLOR ONLY.
                         */
                        color: active ? ORANGE : BLACK,

                        backgroundColor: 'transparent !important',

                        textDecoration: 'none',

                        borderBottom: `1px solid ${BORDER}`,

                        borderRadius: 0,

                        transition: 'color 250ms ease, transform 200ms ease',

                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: ORANGE
                        },

                        '&:active': {
                          transform: 'translateX(4px)'
                        }
                      }}>
                      <Typography
                        sx={{
                          flex: 1,

                          fontFamily: 'Raleway, sans-serif',

                          fontSize: 14,

                          fontWeight: active ? 600 : 500,

                          lineHeight: 1.4,

                          color: 'inherit'
                        }}>
                        {item.text}
                      </Typography>

                      {hasChildren && (
                        <ChevronRightIcon
                          sx={{
                            fontSize: 22,
                            color: 'inherit',

                            transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)'
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      {/* ======================================================== */}
      {/* MOBILE HEADER                                             */}
      {/* ======================================================== */}

      {isMobile && <MobileHeader />}

      {/* ======================================================== */}
      {/* MOBILE MENU                                               */}
      {/* ======================================================== */}

      {isMobile && <MobileMenu />}

      {/* ======================================================== */}
      {/* DESKTOP SIDE NAV                                          */}
      {/* ======================================================== */}

      {!isMobile && (
        <Box
          sx={{
            position: 'relative',

            width: open ? 280 : 72,
            height: '100vh',

            flexShrink: 0,

            backgroundColor: WHITE,

            borderRight: `1px solid ${BORDER}`,

            display: 'flex',
            flexDirection: 'column',

            transition: 'width 350ms cubic-bezier(0.16, 1, 0.3, 1)',

            overflow: 'hidden',

            zIndex: 10,

            boxSizing: 'border-box'
          }}>
          {/* LOGO / TOGGLE */}
          <Box
            sx={{
              height: 82,
              minHeight: 82,

              flexShrink: 0,

              display: 'flex',
              alignItems: 'center',

              justifyContent: open ? 'space-between' : 'center',

              px: open ? 2 : 1.5,

              borderBottom: `1px solid ${BORDER}`
            }}>
            {open && (
              <Box
                component="img"
                src={Logo}
                alt="Fairtree"
                sx={{
                  width: 200,
                  height: 'auto',

                  maxHeight: 48,

                  objectFit: 'contain',
                  objectPosition: 'left center'
                }}
              />
            )}

            <IconButton
              onClick={handleDesktopToggle}
              aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
              disableRipple
              size="small"
              sx={{
                color: BLACK,

                transition: 'color 250ms ease, transform 250ms ease',

                '&:hover': {
                  backgroundColor: 'transparent',

                  color: ORANGE
                },

                '&:active': {
                  transform: 'scale(0.9)'
                }
              }}>
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>

          {/* NAVIGATION */}
          <Box
            sx={{
              flex: 1,

              minHeight: 0,

              overflowY: 'auto',
              overflowX: 'hidden',

              px: open ? 1.5 : 1,
              py: 1.5,

              scrollbarWidth: 'thin',

              '&::-webkit-scrollbar': {
                width: 5
              },

              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.18)'
              },

              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent'
              }
            }}>
            <List disablePadding>
              {navItems.map((item) => {
                const ParentIcon = navIcons[item.title];

                const hasChildren = item.items?.length > 0;

                const parentActive = isParentActive(item) || isParentLinkActive(item);

                const itemOpen = !!openItems[item.title];

                return (
                  <Box key={item.title}>
                    {/* PARENT */}
                    <ListItemButton
                      component={!hasChildren ? NavLink : 'button'}
                      to={!hasChildren ? item.link : undefined}
                      onClick={() => handleParentClick(item)}
                      sx={{
                        minHeight: 52,

                        px: open ? 1.5 : 1,

                        mb: 0.5,

                        justifyContent: open ? 'initial' : 'center',

                        /*
                         * ACTIVE = COLOR ONLY.
                         */
                        color: parentActive ? ORANGE : BLACK,

                        backgroundColor: 'transparent !important',

                        textDecoration: 'none',

                        transition: 'color 250ms ease, transform 200ms ease',

                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: ORANGE
                        },

                        '&:active': {
                          transform: 'translateX(3px)'
                        }
                      }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 42,
                          width: 42,
                          maxWidth: 42,
                          flexShrink: 0,
                          justifyContent: 'center',
                          color: 'inherit'
                        }}>
                        {ParentIcon && (
                          <ParentIcon
                            sx={{
                              width: 22,
                              height: 22,
                              fontSize: 22,
                              flexShrink: 0
                            }}
                          />
                        )}
                      </ListItemIcon>
                      {open && (
                        <>
                          <Typography
                            sx={{
                              flex: 1,

                              fontFamily: 'Raleway, sans-serif',

                              fontSize: 14,

                              fontWeight: parentActive ? 600 : 500,

                              color: 'inherit',

                              whiteSpace: 'nowrap',

                              overflow: 'hidden',

                              textOverflow: 'ellipsis'
                            }}>
                            {item.title}
                          </Typography>

                          {hasChildren &&
                            (itemOpen ? (
                              <ExpandMoreIcon
                                sx={{
                                  fontSize: 20,
                                  color: 'inherit',

                                  transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)'
                                }}
                              />
                            ) : (
                              <ChevronRightIcon
                                sx={{
                                  fontSize: 20,
                                  color: 'inherit',

                                  transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)'
                                }}
                              />
                            ))}
                        </>
                      )}
                    </ListItemButton>

                    {/* DESKTOP CHILDREN */}
                    {open && hasChildren && (
                      <Box
                        sx={{
                          display: itemOpen ? 'block' : 'none',

                          pl: 2,

                          mb: 0.75
                        }}>
                        <List disablePadding>
                          {item.items.map((subItem) => {
                            const active = isChildActive(subItem.link);

                            return (
                              <ListItemButton
                                key={subItem.link}
                                component={NavLink}
                                to={subItem.link}
                                sx={{
                                  position: 'relative',

                                  minHeight: 42,

                                  py: 0.75,
                                  px: 1.5,

                                  mb: 1,

                                  alignItems: 'center',

                                  /*
                                   * ACTIVE =
                                   * COLOR ONLY.
                                   */
                                  color: active ? ORANGE : BLACK,

                                  backgroundColor: 'transparent !important',

                                  textDecoration: 'none',

                                  transition: 'color 250ms ease, transform 200ms ease',

                                  '&:hover': {
                                    backgroundColor: 'transparent',

                                    color: ORANGE
                                  },

                                  '&:active': {
                                    transform: 'translateX(4px)'
                                  }
                                }}>
                                <ListItemIcon
                                  sx={{
                                    minWidth: 36,

                                    justifyContent: 'center',

                                    color: 'inherit'
                                  }}>
                                  {subItem.image ? (
                                    <Box
                                      component="img"
                                      src={subItem.image}
                                      alt=""
                                      sx={{
                                        width: 20,
                                        height: 20,

                                        objectFit: 'contain',

                                        filter: active
                                          ? 'brightness(0) saturate(100%) invert(42%) sepia(39%) saturate(1073%) hue-rotate(338deg) brightness(88%) contrast(88%)'
                                          : 'none',

                                        transition: 'filter 250ms ease'
                                      }}
                                    />
                                  ) : null}
                                </ListItemIcon>

                                <Typography
                                  sx={{
                                    fontFamily: 'Raleway, sans-serif',

                                    fontSize: 12.5,

                                    fontWeight: active ? 600 : 500,

                                    lineHeight: 1.3,

                                    color: 'inherit',

                                    whiteSpace: 'nowrap',

                                    overflow: 'hidden',

                                    textOverflow: 'ellipsis'
                                  }}>
                                  {subItem.text}
                                </Typography>
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </List>
          </Box>

          {/* MY PROFILE */}
          <Box
            sx={{
              flexShrink: 0,

              px: open ? 1.5 : 1,
              py: 1.5,

              borderTop: `1px solid ${BORDER}`,

              backgroundColor: WHITE
            }}>
            <ListItemButton
              component={NavLink}
              to="/user/profile"
              sx={{
                minHeight: 52,

                px: open ? 1.5 : 1,

                justifyContent: open ? 'initial' : 'center',

                /*
                 * ACTIVE = COLOR ONLY.
                 */
                color: profileActive ? ORANGE : BLACK,

                backgroundColor: 'transparent !important',

                textDecoration: 'none',

                transition: 'color 250ms ease, transform 200ms ease',

                '&:hover': {
                  backgroundColor: 'transparent',

                  color: ORANGE
                },

                '&:active': {
                  transform: 'translateX(3px)'
                }
              }}>
              <ListItemIcon
                sx={{
                  minWidth: open ? 42 : 0,

                  justifyContent: 'center',

                  color: 'inherit'
                }}>
                <PersonOutlinedIcon
                  sx={{
                    fontSize: 23
                  }}
                />
              </ListItemIcon>

              {open && (
                <Typography
                  sx={{
                    fontFamily: 'Raleway, sans-serif',

                    fontSize: 14,

                    fontWeight: profileActive ? 600 : 500,

                    color: 'inherit',

                    whiteSpace: 'nowrap'
                  }}>
                  My Profile
                </Typography>
              )}
            </ListItemButton>
          </Box>
        </Box>
      )}
    </>
  );
};

export default SideNav;
